/**
 * The protocol-defined adverse event (PDAE) / adverse event of special interest (AESI) form asks which of the
 * PROTOCOL's own events of interest occurred (review item 18, batch 2, C; Jim 2026-10-18).
 *
 * The deterministic pass does ONE thing, the way the DLT pass does (crf/dltForm.ts): it ENSURES one required
 * checklist (variable EOICAT) whose options are exactly analysis.eventsOfInterest's labels, plus the
 * awareness and reporting dates when the protocol states a reporting clock for these events. It never
 * removes, renames or edits any other question, and it never touches a checklist or date the form already
 * has. With no usable fact it changes nothing at all.
 *
 * Source of truth: analysis.eventsOfInterest (agents/eventsOfInterestFacts.ts). It is acted on only when it
 * is grounded in the PROTOCOL TEXT: checked here against the whole stored text when the generator has it,
 * otherwise only when the analysis grounded it in the text it read (groundedInProtocol).
 */
import { groundEventsOfInterestInProtocol, labelGroundedIn, normaliseEventsOfInterest, type EventsOfInterestFacts } from "../agents/eventsOfInterestFacts";
import type { AuthVariable } from "./crfProtocolReading";

export const EOI_CHECKLIST_VAR = "EOICAT";
export const EOI_OTHER_VAR = "EOICATSP";
export const EOI_AWARE_VAR = "EOIAWDT";
export const EOI_SPONSOR_VAR = "EOISPDT";
export const EOI_IRB_VAR = "EOIIRBDT";
export const EOI_CHECKLIST_TEXT = "Which protocol-defined event of interest occurred?";
const OTHER_LABEL = "Other, specify";
/** A reporting clock longer than this is left out of the question text (it is in the protocol). */
const MAX_CLOCK_IN_TEXT = 120;

/**
 * The words that make a form name an events-of-interest form. Written for RAW names (any separator), so the
 * AE house matcher (crf/houseFormsData.ts) can exclude exactly these names with the same pattern.
 */
export const EVENT_OF_INTEREST_NAME_PATTERN = String.raw`special[\W_]*interest|clinical[\W_]*interest|events?[\W_]+of[\W_]+interest|protocol[\W_]*defined[\W_]*(?:adverse[\W_]*)?events?|\bpdaes?\b|\baesis?\b|\becis?\b`;
const EOI_NAME = new RegExp(EVENT_OF_INTEREST_NAME_PATTERN, "i");
const EOI_NAME_ALL = new RegExp(EVENT_OF_INTEREST_NAME_PATTERN, "gi");

/**
 * The words that make a form name an immune-related (or immune-mediated) adverse event form, for RAW names.
 * Such a form is NOT the AE log (measured on Clinical Dev: "Immune-Related Adverse Event (irAE) Management"
 * was copied from the AE log), so the AE house matcher excludes it and it is generated instead. It is NOT an
 * events-of-interest form either: it gets the events checklist only when its name is itself an
 * events-of-interest name, and then the protocol's fact decides the checklist (review item 18, batch 2, C).
 */
export const IMMUNE_RELATED_AE_NAME_PATTERN = String.raw`immune[\W_]*(?:related|mediated)|\biraes?\b|\bimaes?\b`;
const IRAE_NAME = new RegExp(IMMUNE_RELATED_AE_NAME_PATTERN, "i");
export function isImmuneRelatedAeForm(formName?: string | null): boolean {
  return IRAE_NAME.test(String(formName ?? ""));
}

/** The name mentions events of interest at all (the raw words, before the combined-name guard). */
export function namesEventsOfInterest(formName?: string | null): boolean {
  return EOI_NAME.test(String(formName ?? ""));
}

/**
 * A COMBINED name: the plain AE log joined to events of interest ("Adverse Events (including AESI)",
 * "Adverse Events / AESI", "Adverse Events and Events of Special Interest"). When unsure, it is the AE log:
 * it keeps the AE house form and gets no events checklist (checker, 2026-10-18). The test: once the
 * events-of-interest words are taken out, a plain "adverse event(s)" remains that is not "adverse events of
 * ...", and the name joins two things ("and", "&", "/", "+", "including", "with", "plus").
 */
export function isCombinedAeLogName(formName?: string | null): boolean {
  const n = String(formName ?? "");
  if (!EOI_NAME.test(n)) return false;
  const rest = n.replace(EOI_NAME_ALL, " | ");
  const plainAe = /adverse[\W_]+events?\b(?![\W_]+of\b)/i.test(rest);
  const joined = /\band\b|&|\/|\+|\bincl(?:uding|\.)?|\bwith\b|\bplus\b/i.test(n);
  return plainAe && joined;
}

/**
 * A form that records the protocol's events of interest. A serious adverse event form is not one, even when it
 * mentions special interest: it takes the SAE house form. A combined AE-log name is not one either. The
 * study-level coverage rule counts by this same test, so a name that fails here would get a duplicate form
 * added AND miss its checklist.
 */
export function isEventOfInterestForm(formName?: string | null): boolean {
  const n = String(formName ?? "");
  return EOI_NAME.test(n) && !/serious/i.test(n) && !isCombinedAeLogName(n);
}

export interface EventsOfInterestChange {
  varName: string;
  action: "checklist_added" | "checklist_updated" | "other_field_added" | "date_added" | "not_applied";
  why: string;
}

export interface EventsOfInterestContext {
  analysis: Record<string, unknown> | null;
  formName?: string;
  protocolText?: string;
  /** True when protocolText is the WHOLE stored protocol: only then is the fact checked against it here. */
  protocolTextComplete?: boolean;
}

const factsOf = (analysis: Record<string, unknown> | null | undefined): EventsOfInterestFacts => normaliseEventsOfInterest(analysis?.eventsOfInterest);

/**
 * The facts this form may act on, or why not. The fact must be grounded in the PROTOCOL TEXT: checked here
 * against the whole stored text when the generator has it, otherwise only a fact the analysis already
 * grounded in the text it read (groundedInProtocol). A fact grounded only in the model's own definitions is
 * never acted on (checker, 2026-10-18).
 */
export function usableEventsOfInterest(ctx: EventsOfInterestContext): { facts: EventsOfInterestFacts | null; why: string } {
  const f = factsOf(ctx.analysis);
  if (!f.stated) return { facts: null, why: "" };
  const text = String(ctx.protocolText ?? "");
  if (ctx.protocolTextComplete === true && text.trim()) {
    const g = groundEventsOfInterestInProtocol(f, text);
    return g.stated ? { facts: g, why: "" } : { facts: null, why: `not grounded in the protocol text: ${g.reason}` };
  }
  if (f.groundedInProtocol) return { facts: f, why: "" };
  return { facts: null, why: "the events were not checked against the protocol text, and the whole protocol text is not available here" };
}

/** The short instruction the form-writing prompt carries on an events-of-interest form. Empty elsewhere. */
export function eventsOfInterestPromptBlock(ctx: EventsOfInterestContext): string {
  if (!isEventOfInterestForm(ctx.formName)) return "";
  const f = usableEventsOfInterest(ctx).facts;
  const lines: string[] = [];
  lines.push("This form records the protocol's own events of interest. It is not a copy of the adverse event log: do not repeat the adverse event log's seriousness, severity, causality, action and outcome questions unless the protocol asks for them on this form.");
  if (f) {
    lines.push(`The protocol designates these events: ${f.events.map((e, i) => `${i + 1}. ${e.label} (${e.definition})`).join(" ")}`);
    lines.push(`The question "${EOI_CHECKLIST_TEXT}" (variable ${EOI_CHECKLIST_VAR}) lists exactly these events${f.listOpen === true ? `, plus "${OTHER_LABEL}"` : ", and no others"}.`);
    if (f.reporting) lines.push(`The protocol's reporting deadline for these events: ${f.reporting.clock}.`);
  } else {
    lines.push("The protocol analysis holds no list of these events grounded in the protocol text. Do not invent any.");
  }
  return ["## THE EVENTS-OF-INTEREST FORM (it outranks every default)", ...lines].join("\n");
}

const isDate = (v: AuthVariable) => String(v.responseType) === "date";
const textOf = (v: AuthVariable) => String(v.questionText ?? "");
const isSelectVar = (v: AuthVariable) => /select|radio|coded|checkbox/.test(String(v.responseType));

/**
 * An existing checklist of these events that is not ours: a select whose variable is an event category or
 * type (AESI / PDAE / ECI / EOI prefix ending CAT, TYP or TYPE), wherever it sits on the form, or a select with
 * at least two options that between them cover every protocol label word by word. It is left exactly as it
 * is: when unsure, change nothing.
 */
// Re-check (checker, 2026-10-18): only an event category / type variable, or a select whose options are the
// event list, is the existing checklist. Another AESI or PDAE select (severity, grade, relationship) is not.
const CHECKLIST_VAR_NAME = /^(?:AESI|PDAE|ECI|EOI)[A-Z0-9_]*?(?:CAT|TYPE?)$/i;
function existingChecklistIndex(list: AuthVariable[], f: EventsOfInterestFacts): number {
  const byName = list.findIndex((v) => isSelectVar(v) && CHECKLIST_VAR_NAME.test(String(v.varName ?? "")));
  if (byName >= 0) return byName;
  return list.findIndex((v) => {
    if (!isSelectVar(v)) return false;
    const opts = (Array.isArray(v.options) ? v.options : []).map((o) => String(o.label ?? ""));
    return opts.length >= 2 && f.events.every((e) => opts.some((o) => labelGroundedIn(e.label, o)));
  });
}

const AWARE_WORDS = /aware|knowledge|became known|\blearn(?:ed|t)? of/i;
/** Any date about reporting counts for the Sponsor date: "Date reported", "Reporting date", "Report date". */
const REPORT_WORDS = /sponsor|\breport/i;
const IRB_WORDS = /\birb\b|ethic|\biec\b|\bec\b|review board/i;

export function applyEventsOfInterestForm<V extends AuthVariable>(variables: V[], ctx: EventsOfInterestContext): { variables: V[]; changes: EventsOfInterestChange[] } {
  if (!isEventOfInterestForm(ctx.formName)) return { variables, changes: [] };
  const usable = usableEventsOfInterest(ctx);
  if (!usable.facts) {
    return { variables, changes: usable.why ? [{ varName: EOI_CHECKLIST_VAR, action: "not_applied", why: usable.why }] : [] };
  }
  const f = usable.facts;
  const changes: EventsOfInterestChange[] = [];
  const withOther = f.listOpen === true;
  const options: Array<{ code: number; label: string }> = f.events.map((e, i) => ({ code: i + 1, label: e.label }));
  if (withOther) options.push({ code: options.length + 1, label: OTHER_LABEL });
  const section = f.events.find((e) => e.section)?.section;
  const codesFrom = `protocol events of interest${section ? ` (section ${section})` : ""}`;

  const list = variables.slice();
  let anchor = list.findIndex((v) => v.varName === EOI_CHECKLIST_VAR);
  let ours = anchor >= 0;
  if (ours) {
    list[anchor] = { ...list[anchor], responseType: "multi_select", options, required: true, codesFrom } as V;
    changes.push({ varName: EOI_CHECKLIST_VAR, action: "checklist_updated", why: `options set to the ${f.events.length} protocol events of interest` });
  } else {
    anchor = existingChecklistIndex(list, f);
    if (anchor < 0) {
      const checklist = { varName: EOI_CHECKLIST_VAR, questionText: EOI_CHECKLIST_TEXT, responseType: "multi_select", options, unit: "", required: true, codesFrom } as unknown as V;
      const term = list.findIndex((v) => /TERM$/i.test(v.varName));
      anchor = term >= 0 ? term + 1 : 0;
      list.splice(anchor, 0, checklist);
      ours = true;
      changes.push({ varName: EOI_CHECKLIST_VAR, action: "checklist_added", why: `added: ${f.events.length} protocol events of interest${withOther ? " plus Other, specify" : ""}` });
    }
  }
  let after = anchor;
  const insert = (v: V, change: EventsOfInterestChange) => {
    list.splice(after + 1, 0, v);
    after += 1;
    changes.push(change);
  };
  // The Other field belongs to OUR checklist only: an existing checklist is not ours to extend.
  if (ours && withOther && !list.some((v) => v.varName === EOI_OTHER_VAR)) {
    insert({ varName: EOI_OTHER_VAR, questionText: `If "${OTHER_LABEL}", describe the event`, responseType: "text", options: [], unit: "" } as unknown as V,
      { varName: EOI_OTHER_VAR, action: "other_field_added", why: "the protocol says other events may also be reported as events of interest" });
  }
  const r = f.reporting;
  if (r) {
    const date = (varName: string, questionText: string): V => ({ varName, questionText, responseType: "date", options: [], unit: "" } as unknown as V);
    const has = (name: string, words: RegExp) => list.some((v) => v.varName === name || (isDate(v) && words.test(textOf(v))));
    const why = `the protocol states a reporting deadline for these events (${r.clock})`;
    if (!has(EOI_AWARE_VAR, AWARE_WORDS)) insert(date(EOI_AWARE_VAR, "Date the site became aware of the event"), { varName: EOI_AWARE_VAR, action: "date_added", why });
    if (r.toSponsor && !has(EOI_SPONSOR_VAR, REPORT_WORDS)) {
      const clock = r.clock.length <= MAX_CLOCK_IN_TEXT ? ` (the protocol requires: ${r.clock})` : "";
      insert(date(EOI_SPONSOR_VAR, `Date reported to the Sponsor${clock}`), { varName: EOI_SPONSOR_VAR, action: "date_added", why });
    }
    if (r.toIrb && !has(EOI_IRB_VAR, IRB_WORDS)) insert(date(EOI_IRB_VAR, "Date reported to the IRB/EC"), { varName: EOI_IRB_VAR, action: "date_added", why });
  }
  return { variables: list, changes };
}
